import {
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IStoreMapLocation } from '../../../interfaces/Store';
import { useGoogleMapsApi } from '../../../hooks/useGoogleMapsApi';
import { importGoogleMapsLibrary } from '../../../utils/googleMapsLoader';
import WashdayClientSingleton from '../../../services/WashdayClientSingleton';
import type {
  GoogleMapInstance,
  GoogleMapMouseEvent,
  GoogleMapsApi,
  GoogleMarkerInstance,
} from '../../../types/googleMaps';

const DEFAULT_CENTER = {
  lat: 19.4326,
  lng: -99.1332,
};
const MIN_AUTOCOMPLETE_QUERY_LENGTH = 3;
const AUTOCOMPLETE_DEBOUNCE_MS = 350;

type ConfirmPayload = {
  lat: number;
  lng: number;
  source: 'manual' | 'places' | 'geocoded';
  formattedAddress?: string;
  placeId?: string;
  address?: ParsedAddress;
};

type ParsedAddress = {
  street?: string;
  number?: string;
  neighborhood?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  country?: string;
};

type AutocompleteSuggestion = {
  placeId: string;
  placeResourceName: string;
  text: string;
};

type PlaceDetails = {
  placeId: string;
  placeResourceName: string;
  formattedAddress?: string;
  latLng: { lat: number; lng: number };
  address?: ParsedAddress;
};

type GeocodeResult = {
  placeId?: string;
  formattedAddress?: string;
  latLng: { lat: number; lng: number };
  address?: ParsedAddress;
};

interface StoreMapLocationPickerModalProps {
  opened: boolean;
  onClose: () => void;
  initialMapLocation?: IStoreMapLocation | null;
  onConfirm: (payload: ConfirmPayload) => void;
  labelsNamespace?: 'StoresPage' | 'CustomersPage';
}

function isValidLatLng(lat?: number | null, lng?: number | null) {
  return (
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90 &&
    typeof lng === 'number' &&
    Number.isFinite(lng) &&
    lng >= -180 &&
    lng <= 180
  );
}

function createSessionToken() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function unwrapProxyData<T>(response: any): T {
  return (response?.data?.data ?? response?.data ?? response) as T;
}

const StoreMapLocationPickerModal = ({
  opened,
  onClose,
  initialMapLocation,
  onConfirm,
  labelsNamespace = 'StoresPage',
}: StoreMapLocationPickerModalProps) => {
  const { t } = useTranslation(['StoresPage', 'CustomersPage', 'CommonButtons']);
  const { isLoaded, isLoading, isError, errorMessage } = useGoogleMapsApi(opened);
  const mapRef = useRef<GoogleMapInstance | null>(null);
  const markerRef = useRef<GoogleMarkerInstance | null>(null);
  const latestPositionRef = useRef(DEFAULT_CENTER);
  const autocompleteRequestIdRef = useRef(0);
  const suppressedAutocompleteTextRef = useRef<string | null>(null);
  const [mapContainerNode, setMapContainerNode] = useState<HTMLDivElement | null>(null);
  const [hasManualAdjustment, setHasManualAdjustment] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [selectedPosition, setSelectedPosition] = useState(DEFAULT_CENTER);
  const [sessionToken, setSessionToken] = useState(createSessionToken);
  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<PlaceDetails | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const initialPosition = useMemo<{ lat: number; lng: number }>(() => {
    const lng = initialMapLocation?.coordinates?.[0];
    const lat = initialMapLocation?.coordinates?.[1];

    if (isValidLatLng(lat, lng)) {
      return { lat: lat as number, lng: lng as number };
    }

    return DEFAULT_CENTER;
  }, [initialMapLocation]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    setSelectedPosition(initialPosition);
    latestPositionRef.current = initialPosition;
    setHasManualAdjustment(false);
    setMapError(null);
    const initialSearchText = initialMapLocation?.formattedAddress || '';
    suppressedAutocompleteTextRef.current = initialSearchText.trim() || null;
    setSearchText(initialSearchText);
    setSuggestions([]);
    setSelectedPlace(null);
    setSessionToken(createSessionToken());
  }, [opened, initialPosition]);

  const mapContainerRef = useCallback((node: HTMLDivElement | null) => {
    setMapContainerNode(node);
  }, []);

  useEffect(() => {
    if (!opened || !isLoaded || !mapContainerNode) {
      return;
    }

    let cancelled = false;
    let dragListener: ReturnType<GoogleMarkerInstance['addListener']> | null = null;
    let clickListener: ReturnType<GoogleMapInstance['addListener']> | null = null;
    let animationFrameId: number | null = null;
    let resizeTimeoutId: number | null = null;

    const initializeMap = async () => {
      try {
        await importGoogleMapsLibrary<Record<string, unknown>>('maps');
      } catch (error) {
        if (!cancelled) {
          setMapError(
            error instanceof Error
              ? error.message
              : t(`${labelsNamespace}:googleMapsUnavailable`),
          );
        }
        return;
      }

      if (cancelled || !mapContainerNode) {
        return;
      }

      const googleMaps = window.google as GoogleMapsApi | undefined;
      if (typeof googleMaps?.maps?.Map !== 'function' || typeof googleMaps.maps.Marker !== 'function') {
        setMapError(t(`${labelsNamespace}:googleMapsUnavailable`));
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        if (cancelled || !mapContainerNode) {
          return;
        }

        const currentPosition = latestPositionRef.current;
        const map = new googleMaps.maps.Map(mapContainerNode, {
          center: currentPosition,
          zoom: currentPosition === DEFAULT_CENTER ? 11 : 16,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        const marker = new googleMaps.maps.Marker({
          map,
          position: currentPosition,
          draggable: true,
        });
        mapRef.current = map;
        markerRef.current = marker;

        const recenterMap = (position: { lat: number; lng: number }) => {
          googleMaps.maps.event.trigger?.(map, 'resize');
          if (typeof map.setCenter === 'function') {
            map.setCenter(position);
            return;
          }

          map.panTo(position);
        };

        const updatePosition = (lat: number, lng: number, manualAdjustment: boolean) => {
          const nextPosition = { lat, lng };
          setSelectedPosition(nextPosition);
          latestPositionRef.current = nextPosition;
          marker.setPosition(nextPosition);
          map.panTo(nextPosition);
          if (manualAdjustment) {
            setHasManualAdjustment(true);
            setSelectedPlace(null);
          }
        };

        dragListener = marker.addListener('dragend', (event: GoogleMapMouseEvent) => {
          const lat = event.latLng?.lat();
          const lng = event.latLng?.lng();
          if (typeof lat !== 'number' || typeof lng !== 'number') return;
          updatePosition(lat, lng, true);
        });

        clickListener = map.addListener('click', (event: GoogleMapMouseEvent) => {
          const lat = event.latLng?.lat();
          const lng = event.latLng?.lng();
          if (typeof lat !== 'number' || typeof lng !== 'number') return;
          updatePosition(lat, lng, true);
        });

        recenterMap(latestPositionRef.current);
        resizeTimeoutId = window.setTimeout(() => {
          if (cancelled) {
            return;
          }

          recenterMap(latestPositionRef.current);
        }, 250);

        setMapError(null);
      });
    };

    void initializeMap();

    return () => {
      cancelled = true;

      if (animationFrameId !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(animationFrameId);
      }

      if (resizeTimeoutId !== null) {
        window.clearTimeout(resizeTimeoutId);
      }

      if (dragListener) {
        window.google?.maps?.event?.removeListener(dragListener);
      }

      if (clickListener) {
        window.google?.maps?.event?.removeListener(clickListener);
      }

      markerRef.current?.setMap(null);
      markerRef.current = null;
      mapRef.current = null;
    };
  }, [opened, isLoaded, initialPosition, mapContainerNode, labelsNamespace, t]);

  useEffect(() => {
    markerRef.current?.setPosition(selectedPosition);
    mapRef.current?.panTo(selectedPosition);
  }, [selectedPosition]);

  useEffect(() => {
    const trimmedSearchText = searchText.trim();

    if (!opened || trimmedSearchText.length < MIN_AUTOCOMPLETE_QUERY_LENGTH) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    if (suppressedAutocompleteTextRef.current === trimmedSearchText) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    const requestId = autocompleteRequestIdRef.current + 1;
    autocompleteRequestIdRef.current = requestId;
    const timeoutId = window.setTimeout(() => {
      setIsSearching(true);
      const client = WashdayClientSingleton.getInstance();
      void client.googleMaps
        .autocomplete({
          input: trimmedSearchText,
          sessionToken,
        })
        .then((response: any) => {
          if (autocompleteRequestIdRef.current !== requestId) return;
          const data = unwrapProxyData<{ suggestions?: AutocompleteSuggestion[] }>(response);
          setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
        })
        .catch(() => {
          if (autocompleteRequestIdRef.current !== requestId) return;
          setSuggestions([]);
        })
        .finally(() => {
          if (autocompleteRequestIdRef.current !== requestId) return;
          setIsSearching(false);
        });
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [opened, searchText, sessionToken]);

  const movePinToPosition = useCallback((position: { lat: number; lng: number }) => {
    setSelectedPosition(position);
    latestPositionRef.current = position;
    markerRef.current?.setPosition(position);
    mapRef.current?.panTo(position);
  }, []);

  const handleSuggestionSelect = useCallback(
    async (suggestion: AutocompleteSuggestion) => {
      autocompleteRequestIdRef.current += 1;
      suppressedAutocompleteTextRef.current = suggestion.text.trim();
      setSearchText(suggestion.text);
      setSuggestions([]);
      setIsSearching(true);

      try {
        const client = WashdayClientSingleton.getInstance();
        const response = await client.googleMaps.placeDetails({
          sessionToken,
          placeId: suggestion.placeId,
          placeResourceName: suggestion.placeResourceName,
        });
        const details = unwrapProxyData<PlaceDetails>(response);

        if (!isValidLatLng(details?.latLng?.lat, details?.latLng?.lng)) {
          return;
        }

        setSelectedPlace(details);
        setHasManualAdjustment(false);
        movePinToPosition(details.latLng);
        setSessionToken(createSessionToken());
      } finally {
        setIsSearching(false);
      }
    },
    [movePinToPosition, sessionToken],
  );

  const handleConfirm = async () => {
    if (!selectedPosition) return;

    setIsConfirming(true);
    try {
      const canKeepPlacesMetadata =
        !hasManualAdjustment &&
        ((selectedPlace && Boolean(selectedPlace.formattedAddress || selectedPlace.placeId)) ||
          (initialMapLocation?.source === 'places' &&
            Boolean(initialMapLocation.formattedAddress || initialMapLocation.placeId)));

      let reverseGeocodeResult: GeocodeResult | null = null;
      try {
        const client = WashdayClientSingleton.getInstance();
        const response = await client.googleMaps.reverseGeocode({
          latLng: selectedPosition,
        });
        const data = unwrapProxyData<{ result?: GeocodeResult | null }>(response);
        reverseGeocodeResult = data?.result ?? null;
      } catch {
        reverseGeocodeResult = null;
      }

      const fallbackPlace = selectedPlace || null;
      const formattedAddress =
        reverseGeocodeResult?.formattedAddress ||
        fallbackPlace?.formattedAddress ||
        initialMapLocation?.formattedAddress;
      const placeId =
        reverseGeocodeResult?.placeId ||
        fallbackPlace?.placeId ||
        initialMapLocation?.placeId;

      onConfirm({
        lat: selectedPosition.lat,
        lng: selectedPosition.lng,
        source: canKeepPlacesMetadata ? 'places' : 'manual',
        ...(formattedAddress
          ? { formattedAddress }
          : {}),
        ...(placeId
          ? { placeId }
          : {}),
        ...(reverseGeocodeResult?.address || fallbackPlace?.address
          ? { address: reverseGeocodeResult?.address || fallbackPlace?.address }
          : {}),
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(`${labelsNamespace}:mapLocationModalTitle`)}
      size="xl"
      centered
    >
      <Stack gap="md">
        <Text size="sm">{t(`${labelsNamespace}:mapLocationModalDescription`)}</Text>

        <div style={{ position: 'relative' }}>
          <TextInput
            label={t(`${labelsNamespace}:mapLocationSearchLabel`)}
            placeholder={t(`${labelsNamespace}:mapLocationSearchPlaceholder`)}
            value={searchText}
            onChange={(event) => {
              suppressedAutocompleteTextRef.current = null;
              setSearchText(event.currentTarget.value);
            }}
            rightSection={isSearching ? <Loader size="xs" /> : null}
          />
          {suggestions.length > 0 ? (
            <Paper
              withBorder
              shadow="md"
              style={{
                position: 'absolute',
                zIndex: 10000000,
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 4,
              }}
            >
              <ScrollArea style={{ maxHeight: 200 }}>
                {suggestions.map((suggestion) => (
                  <Text
                    key={suggestion.placeResourceName || suggestion.placeId}
                    onClick={() => {
                      void handleSuggestionSelect(suggestion);
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    {suggestion.text}
                  </Text>
                ))}
              </ScrollArea>
            </Paper>
          ) : null}
        </div>

        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : null}

        {isError ? (
          <Text c="red" size="sm">
            {errorMessage || t(`${labelsNamespace}:googleMapsUnavailable`)}
          </Text>
        ) : null}

        {!isError && mapError ? (
          <Text c="red" size="sm">
            {mapError}
          </Text>
        ) : null}

        {opened && isLoaded ? (
          <div
            ref={mapContainerRef}
            style={{
              width: '100%',
              minHeight: 360,
              height: 360,
              borderRadius: 8,
              overflow: 'hidden',
              backgroundColor: '#f1f3f5',
            }}
          />
        ) : null}

        <Group justify="space-between">
          <Text size="sm">
            {t('FieldName:latitude')}: {selectedPosition.lat.toFixed(6)}
            {' · '}
            {t('FieldName:longitude')}: {selectedPosition.lng.toFixed(6)}
          </Text>
          <Group>
            <Button type="button" variant="default" onClick={onClose}>
              {t('CommonButtons:cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleConfirm();
              }}
              loading={isConfirming}
              disabled={isLoading || isError || Boolean(mapError)}
            >
              {t(`${labelsNamespace}:useThisLocation`)}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StoreMapLocationPickerModal;
